<script setup lang="ts">
import MiiaFwAuthModal from './AuthModal.vue'
import MiiaFwAlertMessageLight from './AlertMessageLight.vue'
import { useLoadingView } from '../composables/useLoadingView'
import { updatePassword } from '../utils/miiaFwCognito'
import { reactive, ref, useTemplateRef } from 'vue'

/**
 * @module components/miiafw/auth/MiiaFwPasswordUpdate
 * @description パスワードの再設定
 * @vue-data {Boolean} [myModal = false] モーダル制御。trueのときモーダルを開く
 * @vue-data {String} [currentPassword = 空白] 現在のパスワード
 * @vue-data {String} [password = 空白] パスワード
 * @vue-data {String} [password2 = 空白] パスワード（再確認）
 */
const reset = useTemplateRef<HTMLButtonElement>('reset')
const alertMessageLight =
  useTemplateRef<InstanceType<typeof MiiaFwAlertMessageLight>>('alertMessageLight')
const currentPasswordInput = useTemplateRef<HTMLInputElement>('currentPasswordInput')

const { setLoadingView } = useLoadingView()

const emit = defineEmits<{ 'close-top': [] }>()
const myModal = ref(false)
const currentPassword = ref('')
const password = ref('')
const password2 = ref('')
/**
 * フォームの表示制御のためのオブジェクト。
 * hasErrorはフォームの見栄えをAlertにし、
 * alertMessageはフォームの横にアラートメッセージを表示する
 */
const alertView = reactive<{ [key: string]: { hasError: boolean; alertMessage: string } }>({
  /** 現在のパスワード */
  cp: { hasError: false, alertMessage: '' },
  /** パスワード */
  pw: { hasError: false, alertMessage: '' },
  /** 再入力されたパスワード */
  pw2: { hasError: false, alertMessage: '' }
})

/** alertViewを初期化する */
const resetAlertView = () => {
  Object.keys(alertView).forEach(key => {
    alertView[key].hasError = false
    alertView[key].alertMessage = ''
  })
}

/**
 * モーダルを開く
 */
const openModal = (): void => {
  myModal.value = true
}

/**
 * モーダル内の現在のパスワード、パスワード、パスワード（再確認）、エラー表示を消去した後、モーダルを閉じる
 */
const closeMyModal = (): void => {
  currentPassword.value = ''
  password.value = ''
  password2.value = ''
  resetAlertView()
  myModal.value = false
}

/**
 * 一番上のモーダルを閉じる。
 */
const closeModal = (): void => {
  closeMyModal()
  emit('close-top')
}

/**
 * モーダル上でTab操作をした際、モーダル上のオブジェクトを循環できるようにする
 * @param e - キーボードイベント
 */
const firstTab = (e: KeyboardEvent): void => {
  e.preventDefault()
  e.stopPropagation()
  reset.value?.focus()
}

/**
 * モーダル上でShift+Tab操作をした際、モーダル上のオブジェクトを循環できるようにする。
 * @param e - キーボードイベント
 */
const lastTab = (e: KeyboardEvent): void => {
  // Shift + Tabは、Tabの遡りなのでデフォルトイベントのまま
  if (!e.shiftKey) {
    e.preventDefault()
    e.stopPropagation()
    currentPasswordInput.value?.focus()
  }
}

/**
 * モーダル内の現在のパスワード、パスワード（再確認）、エラー表示を消去した後、現在のパスワード入力フォームにフォーカス。
 */
const doReset = (): void => {
  alertMessageLight.value?.deleteAlertMessage()
  password.value = ''
  password2.value = ''
  currentPassword.value = ''
  resetAlertView()
  currentPasswordInput.value?.focus()
}

/**
 * バリデーションチェックを行たのちに、パスワードの更新作業を実行。表示されているエラーを初期化し、各項目に対してバリデーションチェックを行う。問題箇所があったらエラーを表示する。問題が無かったら module:miiafw/mixin/MiiaFwCognitoMixin#updatePasswordへ現在のパスワードと新規パスワードを渡し、module:miiafw/mixin/MiiaFwCognitoMixin#updateUserAttributeへCognitoが変更された日時を渡す。その後モーダルを閉じる
 */
const doSubmit = (): void => {
  // エラー表示初期化
  resetAlertView()
  // バリデーションチェック
  const errorList: string[] = []
  if (currentPassword.value.length < 1) {
    alertView.cp.hasError = true
    alertView.cp.alertMessage = 'Current passwordは必須項目です'
  }
  if (password.value.length < 1) {
    alertView.pw.hasError = true
    alertView.pw.alertMessage = 'New passwordは必須項目です'
  }
  if (password2.value.length < 1) {
    alertView.pw2.hasError = true
    alertView.pw2.alertMessage = 'Re-enter new passwordは必須項目です'
  }
  if (
    password.value.length > 0 &&
    password2.value.length > 0 &&
    password.value !== password2.value
  ) {
    alertView.pw.hasError = true
    alertView.pw2.hasError = true
    errorList.push('パスワードが不一致です。')
  }
  if (errorList.length > 0) {
    alertMessageLight.value?.addAlertMessage(errorList.join('\n'))
  } else if (!alertView.cp.hasError && !alertView.pw.hasError && !alertView.pw2.hasError) {
    // 処理中表示
    setLoadingView(true)
    // パスワード更新処理
    updatePassword(currentPassword.value, password.value)
      .then((/* result */) => {
        // TODO(miia): 元の関数 miiafwCognitolibs_updateUserAttribute が first commit 時点から存在していない
        // Cognitoの更新日時属性を更新
        // updateUserAttribute(result.cognitoUser, { Name: 'updated_at', Value: new Date().getTime().toString() }).then((result) => {
        //   setLoadingView(false) // 必ずコンポーネント消滅前に実行
        closeModal()
        // }).catch((error) => {
        //   alertMessageLight.addAlertMessage('・' + error.message)
        setLoadingView(false) // 必ずコンポーネント消滅前に実行
        // })
      })
      .catch(error => {
        if (error.message.match(/previousPassword/) || error.message.match(/password/)) {
          alertView.cp.hasError = true
        }
        if (error.message.match(/proposedPassword/) || error.message.match(/Password/)) {
          alertView.pw.hasError = true
          alertView.pw2.hasError = true
        }
        alertMessageLight.value?.addAlertMessage(error.message)
        setLoadingView(false) // 必ずコンポーネント消滅前に実行
      })
  }

  defineExpose({
    // data
    myModal,
    currentPassword,
    password,
    password2,
    alertView,

    // methods
    openModal,
    closeMyModal,
    closeModal,
    firstTab,
    lastTab,
    doReset,
    doSubmit
  })
}
</script>

<template>
  <!-- @closeを指定すると枠外クリック時にモーダルウィンドウを閉じる。 -->
  <!-- <MyModal @close="closeModal" v-if="modal"> -->
  <MiiaFwAuthModal v-if="myModal" @push-ctl-delete="doReset">
    <!-- default スロットコンテンツ -->
    <h4 style="font-size: 1.1rem; font-weight: 500">パスワード更新</h4>
    <div style="padding: 30px 10px 30px 10px; margin-bottom: 10px; border: 1px solid #bcbcbc">
      <div
        class="form-group"
        style="text-align: left"
        v-bind:class="{ 'has-error': alertView.cp.hasError }"
      >
        <label class="control-label">
          現在のパスワード
          <span v-if="alertView.cp.alertMessage !== ''" class="ff-alert-msg">
            <font-awesome-icon icon="exclamation-circle" />{{ alertView.cp.alertMessage }}
          </span>
        </label>
        <input
          type="password"
          ref="currentPasswordInput"
          v-model="currentPassword"
          class="form-control ff-frm-ctrl"
          tabindex="1"
          v-focus
          autocomplete="off"
          @keydown.shift.tab="firstTab"
        />
      </div>
      <div
        class="form-group"
        style="text-align: left"
        v-bind:class="{ 'has-error': alertView.pw.hasError }"
      >
        <label class="control-label">
          新しいパスワード
          <span v-if="alertView.pw.alertMessage !== ''" class="ff-alert-msg">
            <font-awesome-icon icon="exclamation-circle" />{{ alertView.pw.alertMessage }}
          </span>
        </label>
        <input
          type="password"
          v-model="password"
          class="form-control ff-frm-ctrl"
          tabindex="2"
          autocomplete="new-password"
        />
      </div>
      <div
        class="form-group"
        style="text-align: left"
        v-bind:class="{ 'has-error': alertView.pw2.hasError }"
      >
        <label class="control-label">
          もう一度新しいパスワードを入力してください。
          <div
            v-if="alertView.pw2.alertMessage !== ''"
            class="ff-alert-msg"
            style="margin-left: 0px"
          >
            <font-awesome-icon icon="exclamation-circle" />{{ alertView.pw2.alertMessage }}
          </div>
        </label>
        <input
          type="password"
          v-model="password2"
          class="form-control ff-frm-ctrl"
          tabindex="3"
          autocomplete="new-password"
        />
      </div>
      <div style="text-align: right">
        <button class="btn ff-btn-primary" tabindex="4" @click="doSubmit">
          <font-awesome-icon icon="edit" />
          &nbsp;パスワード更新
        </button>
      </div>
    </div>
    <MiiaFwAlertMessageLight ref="alertMessageLight"></MiiaFwAlertMessageLight>
    <!-- /default -->
    <!-- footer スロットコンテンツ -->
    <template #slot> </template>
  </MiiaFwAuthModal>
</template>

<style lang="scss" scoped>
:deep(#miiafwauthmodal-close) {
  visibility: hidden;
}
</style>
